import { foundation } from '../../ui';
import { guardRowActivate } from './actionMenuClickGuard';
import { GROUPED_GEOMETRY as GROUPED_LIST, groupedCheckColStyle, groupedLeadingSlot } from '../../ui/groupedListGeometry';
import '../../ui/selectedRow.css';

/** Stable min height for mobile bulk list rows: THE shared touch row (one source, groupedListGeometry). */
export function bulkListRowMinHeightStyle() {
  return { minHeight: GROUPED_LIST.touchRow.minHeightPx };
}

/** Flex gap between the select slot and the row content: the shared touch gap. */
export const BULK_LIST_CHECKBOX_CONTENT_GAP_PX = GROUPED_LIST.touchRow.gapPx;

const fc = foundation.color;

/** Lets vertical scroll pass through without double-tap zoom delay. */
export const mobileListRowTouchStyle = {
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'pan-y',
};

/** Stops mobile browsers from focusing tappable rows/buttons and scroll-jumping. */
export function preventTouchFocusScroll(e) {
  // KAN-962: a mouse press that began on a CopyableValue marks itself so the user can still select its text.
  if (e.nativeEvent && e.nativeEvent.cvxKeepSelection) return;
  e.preventDefault();
}

/**
 * Interaction props for mobile list rows inside a scroll container.
 * Uses click (not focus) so scrolling does not shove the list.
 */
export function mobileTappableRowProps({ onActivate, bulkToggle, ariaLabel, disabled = false }) {
  // While bulk-selecting, a selectable row toggles its own selection instead of
  // opening its detail (Gmail/Photos-style) — the caller passes `bulkToggle`.
  const handler = bulkToggle || onActivate;
  if (disabled || typeof handler !== 'function') return {};
  const activate = guardRowActivate(handler);
  return {
    role: 'button',
    tabIndex: -1,
    'aria-label': ariaLabel,
    onMouseDown: preventTouchFocusScroll,
    onClick: activate,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    },
  };
}

/** Flex `gap` between checkbox slot and primary row content (mobile/card rows). */
export function bulkListRowCheckboxContentGapStyle() {
  return { gap: `${BULK_LIST_CHECKBOX_CONTENT_GAP_PX}px` };
}

/** Attribute for grouped mobile list containers (jobs, team, billing). */
export const CVX_STATIC_LIST_ATTR = { 'data-cvx-static-list': true };

/** Stop row-level activation when clicking nested buttons/checkboxes. */
export function stopRowActivate(e) {
  e.stopPropagation();
}

/**
 * Selected-row marker (selectedRow.css, KAN-1054): the attribute alone. Phone/tablet rows spread this AND
 * `bulkListRowSelectionStyle` (which supplies the fill colour var).
 */
export function selectedRowAttr(selected) {
  return selected ? { 'data-cvx-row-selected': '' } : {};
}

/** Attribute + the fill colour var — for a wrapper/`<tr>` that is the "row" and has no selection style of its own. */
export function selectedRowProps(selected) {
  return selected
    ? { ...selectedRowAttr(true), style: { '--cvx-row-sel-fill': fc.brand.primaryMuted } }
    : {};
}

/**
 * Desktop `<tr>` props — row click opens detail; pair with `stopRowActivate` on action cells.
 */
export function desktopClickableRowProps({
  onActivate,
  bulkToggle,
  rowStyle = {},
  enableHover = true,
  hoverBg,
  testId,
  dataAttrs = {},
  selected = false,
}) {
  // Selected (KAN-1054): the row keeps its normal surface; selectedRow.css draws the inset pill from
  // `data-cvx-row-selected`. Hover on a selected row paints nothing, so the pill stays the only blue.
  const baseBg = rowStyle.backgroundColor ?? fc.neutral.surface;
  const hoverPaint = selected ? baseBg : (hoverBg ?? fc.neutral.surfaceMuted);
  // In bulk-select mode the row toggles its selection instead of opening detail.
  const activate = guardRowActivate(bulkToggle || onActivate);
  const { style: edgeStyle, ...edgeAttrs } = selectedRowProps(selected);
  // Rounded rows (KAN-619): browsers never paint border-radius on a <tr>
  // (Chrome + WebKit, verified 2026-10-03), so a row that asks for a radius
  // paints its tint on the CELLS through `--cvx-row-bg`, and index.css rounds
  // the first/last cell (`tr[data-cvx-row-round]`). Non-<tr> rows are unaffected.
  const radius = rowStyle.borderRadius;
  const rounded = radius != null && radius !== 0;
  const radiusCss = typeof radius === 'number' ? `${radius}px` : radius;
  const paintBg = (el, bg) => {
    if (rounded && el.tagName === 'TR') el.style.setProperty('--cvx-row-bg', bg);
    else el.style.backgroundColor = bg;
  };
  return {
    style: {
      ...rowStyle,
      ...edgeStyle,
      ...(rounded ? { '--cvx-row-bg': baseBg, '--cvx-row-radius': radiusCss } : {}),
      cursor: 'pointer',
    },
    ...(rounded ? { 'data-cvx-row-round': '' } : {}),
    ...edgeAttrs,
    onClick: activate,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    },
    role: 'button',
    tabIndex: 0,
    ...(testId ? { 'data-testid': testId } : {}),
    ...dataAttrs,
    ...(enableHover
      ? {
          onMouseEnter: (e) => {
            paintBg(e.currentTarget, hoverPaint);
          },
          onMouseLeave: (e) => {
            paintBg(e.currentTarget, baseBg);
          },
        }
      : {}),
  };
}

/**
 * Bulk row selection (RD-UI-044, KAN-1054) — phone/tablet rows. Sets NO background: it supplies the fill
 * colour var (--cvx-row-sel-fill) and the row also spreads `selectedRowAttr(selected)`, so selectedRow.css
 * draws the same inset pill as the web. No side strip, no border, no padding change - nothing moves.
 * Desktop rows pass `selected` to `desktopClickableRowProps` instead.
 */
export function bulkListRowSelectionStyle(selected, { inBulkMode = false, enforceMinHeight = false } = {}) {
  if (!inBulkMode && !selected) return {};
  return {
    boxSizing: 'border-box',
    ...(selected ? { '--cvx-row-sel-fill': fc.brand.primaryMuted } : {}),
    ...(enforceMinHeight ? bulkListRowMinHeightStyle() : {}),
  };
}

/**
 * THE one checkbox column of every web table (KAN-1016, founder 2026-10-26) — same geometry as the
 * grouped lists: the 18px box's left edge sits at the row's inner edge (`edgePx.web`, 20px) and the
 * first data column's text starts 12px after the box (`checkSlot.web.gap`). The checkbox wrapper is
 * 24px wide with the 18px box centred (3px), so the cell pads `edge - 3`. Width = 20 + 18 + 12 = 50;
 * the first data cell then carries NO left padding of its own (`bulkTableFirstDataTdStyle`).
 * Never set a width / padding on a checkbox `<th>` / `<td>` locally — guard `checkboxColumn.guard.test.js`.
 */
export const TABLE_CHECKBOX_COL_PX = GROUPED_LIST.edgePx.web + GROUPED_LIST.checkSlot.web.box + GROUPED_LIST.checkSlot.web.gap;

/** Web table `<th>` for the bulk checkbox column (numbers live in `groupedCheckColStyle`, one source). */
export function bulkTableCheckboxThStyle() {
  return groupedCheckColStyle(GROUPED_LIST.edgePx.web);
}

/**
 * Web table checkbox column body cell — fixed hit area, no line-height jump when checked.
 * Selection itself is painted on the whole row (`desktopClickableRowProps({ selected })`).
 */
export function bulkTableCheckboxTdStyle() {
  return groupedCheckColStyle(GROUPED_LIST.edgePx.web);
}

/** The first data cell after a checkbox column: the 12px gap is already inside the column. */
export function bulkTableFirstDataTdStyle() {
  return { paddingLeft: 0 };
}

/**
 * Phone / tablet row geometry beside a checkbox (matches the grouped lists' touch rows): the row edge
 * (`edgePx` phone 14 / tablet 16) · 20px box · 10px gap · content. The 44px tap area is invisible.
 */
export function bulkTouchCheckboxSlotStyle() {
  return groupedLeadingSlot(true);
}
export const BULK_TOUCH_CHECKBOX_GAP_PX = GROUPED_LIST.checkSlot.touch.gap;

/** Touch props for native <button> rows inside scrollable lists (e.g. JobCard). */
export function mobileStaticButtonTouchProps() {
  return {
    onMouseDown: preventTouchFocusScroll,
  };
}
